import { SUPPORTED_AUDIO_FORMATS, SupportedAudioFormat } from '@scale/mm-shared';
import { Accordion, SelectMenu, TextField } from '@scale/scaleui';
import { RequiredField } from '@scale/shared-types';
import { MultiTurnInteractionConfiguration } from '@scale/shared/types/chat';
import produce from 'immer';
import ConfigSectionHeader from 'react-scale/components/scalers/tasks/ThreadedInteraction/ChatConfigurationEditor/components/ConfigSectionHeader';

type Props = {
  existingValue: MultiTurnInteractionConfiguration;
  name: string;
  updateProperty: (arg: MultiTurnInteractionConfiguration) => void;
  variant?: 'accordion' | 'config_section_header';
};

const AudioConfiguratorImpl = ({
  existingValue,
  updateProperty,
}: Omit<Props, 'name' | 'variant'>) => {
  return (
    <div className="flex flex-col gap-2 max-w-xl mx-auto">
      <SelectMenu<SupportedAudioFormat | 'none'>
        label="Save Audio Files in this Format"
        options={[
          { id: 'none', name: 'None' },
          ...SUPPORTED_AUDIO_FORMATS.map(format => ({
            id: format,
            name: format,
          })),
        ]}
        selectedOptionId={existingValue.audio?.format ?? 'none'}
        setSelected={selection => {
          updateProperty(
            produce(existingValue, (draft: MultiTurnInteractionConfiguration) => {
              if (selection === 'none') {
                delete draft.audio;
                return;
              }

              if (draft.audio) {
                draft.audio.format = selection;
                return;
              }

              draft.audio = { format: selection };
            }),
          );
        }}
      />

      {
        <>
          <div className="flex justify-between gap-1">
            <TextField
              type="number"
              label="Maximum Volume"
              labelDescription="DB (dBFS) from -70 to 0 (optional)"
              value={existingValue.audio?.maxVolume}
              onChange={e => {
                updateProperty(
                  produce(
                    existingValue,
                    (draft: RequiredField<MultiTurnInteractionConfiguration, 'audio'>) => {
                      if (e.target.value === '') {
                        delete draft.audio?.maxVolume;
                        return;
                      }

                      draft.audio.maxVolume = Number.parseFloat(e.target.value);
                    },
                  ),
                );
              }}
            />
          </div>

          <div className="flex justify-between gap-1">
            <TextField
              type="number"
              label="Minimum Average Volume"
              labelDescription="DB (dBFS) from -70 to 0 (optional)"
              value={existingValue.audio?.minAverageVolume}
              onChange={e => {
                updateProperty(
                  produce(
                    existingValue,
                    (draft: RequiredField<MultiTurnInteractionConfiguration, 'audio'>) => {
                      if (e.target.value === '') {
                        delete draft.audio?.minAverageVolume;
                        return;
                      }

                      draft.audio.minAverageVolume = Number.parseFloat(e.target.value);
                    },
                  ),
                );
              }}
            />

            <TextField
              type="number"
              label="Maximum Average Volume"
              labelDescription="DB (dBFS) from -70 to 0 (optional)"
              value={existingValue.audio?.maxAverageVolume}
              onChange={e => {
                updateProperty(
                  produce(
                    existingValue,
                    (draft: RequiredField<MultiTurnInteractionConfiguration, 'audio'>) => {
                      if (e.target.value === '') {
                        delete draft.audio?.maxAverageVolume;
                        return;
                      }

                      draft.audio.maxAverageVolume = Number.parseFloat(e.target.value);
                    },
                  ),
                );
              }}
            />
          </div>

          <div className="flex justify-between gap-1">
            <TextField
              type="number"
              label="Maximum Ambient Noise"
              labelDescription="DB (dBFS) from -70 to 0 (optional)"
              value={existingValue.audio?.maxAmbientNoiseVolume}
              onChange={e => {
                updateProperty(
                  produce(
                    existingValue,
                    (draft: RequiredField<MultiTurnInteractionConfiguration, 'audio'>) => {
                      if (e.target.value === '') {
                        delete draft.audio?.maxAmbientNoiseVolume;
                        return;
                      }

                      draft.audio.maxAmbientNoiseVolume = Number.parseFloat(e.target.value);
                    },
                  ),
                );
              }}
            />
          </div>
          <div className="flex justify-between gap-1">
            <TextField
              type="number"
              label="Minimum Background Noise Score"
              labelDescription="Model score for background noise in audio - number is 1-5 where 1 is the highest (worst) background noise. 2.9 is a good default."
              value={existingValue.audio?.minBackgroundNoiseScore}
              onChange={e => {
                updateProperty(
                  produce(
                    existingValue,
                    (draft: RequiredField<MultiTurnInteractionConfiguration, 'audio'>) => {
                      if (e.target.value === '') {
                        delete draft.audio?.minBackgroundNoiseScore;
                        return;
                      }

                      draft.audio.minBackgroundNoiseScore = Number.parseFloat(e.target.value);
                    },
                  ),
                );
              }}
            />

            <TextField
              type="number"
              label="Minimum Overall Speech Quality score"
              labelDescription="Model score for overall speech quality in audio - number is 1-5 where 1 is the worst speech quality. 2.4 is a good default."
              value={existingValue.audio?.minOverallSpeechScore}
              onChange={e => {
                updateProperty(
                  produce(
                    existingValue,
                    (draft: RequiredField<MultiTurnInteractionConfiguration, 'audio'>) => {
                      if (e.target.value === '') {
                        delete draft.audio?.minOverallSpeechScore;
                        return;
                      }

                      draft.audio.minOverallSpeechScore = Number.parseFloat(e.target.value);
                    },
                  ),
                );
              }}
            />
          </div>
        </>
      }
    </div>
  );
};

/**
 * todo: idk what accordion vs config section header is for
 *
 * todo: un-memoized for now since updateProperty is not a memoized callback
 */
export const AudioConfigurator = ({ name, variant = 'accordion', ...props }: Props) => {
  if (variant === 'accordion') {
    return (
      <Accordion header={name}>
        <AudioConfiguratorImpl {...props} />
      </Accordion>
    );
  }

  // todo: why would a header wrap the body? i just copied this from another component
  return (
    <ConfigSectionHeader header={name}>
      <AudioConfiguratorImpl {...props} />
    </ConfigSectionHeader>
  );
};
